import { PREVIEW_HIDE_DELAY_MS, PREVIEW_SHOW_DELAY_MS } from "../defaults";
import { createPreviewFetcher } from "./fetcher";
import { scanAndAttach } from "./scanner";
import { resolvePreviewTarget } from "./target";
import { createTrigger, type TriggerHandle } from "./trigger";
import {
  type EmbedConfig,
  type PreviewEntityType,
  type ResolvedPreviewTarget,
  type ThemeOverrides,
} from "./types";

export type { EmbedConfig, PreviewEntityType, ThemeOverrides } from "./types";

interface PreviewController {
  attach(element: HTMLElement, target: ResolvedPreviewTarget): void;
  detach(element: HTMLElement): void;
  destroy(): void;
}

let controller: PreviewController | undefined;

function normalizeConfig(config: EmbedConfig): EmbedConfig {
  return {
    ...config,
    apiBase: config.apiBase.replace(/\/+$/, ""),
    showDelay: config.showDelay ?? PREVIEW_SHOW_DELAY_MS,
    hideDelay: config.hideDelay ?? PREVIEW_HIDE_DELAY_MS,
  };
}

function createController(config: EmbedConfig): PreviewController {
  const attachedTriggers = new Map<HTMLElement, TriggerHandle>();
  const fetcher = createPreviewFetcher(config);
  let scannerCleanup: (() => void) | undefined;
  let readyListener: (() => void) | undefined;

  const attachElement = (
    element: HTMLElement,
    target: ResolvedPreviewTarget,
  ): void => {
    attachedTriggers.get(element)?.destroy();
    const trigger = createTrigger({
      element,
      target,
      config,
      fetcher,
    });
    attachedTriggers.set(element, trigger);
  };

  const detachElement = (element: HTMLElement): void => {
    attachedTriggers.get(element)?.destroy();
    attachedTriggers.delete(element);
  };

  const startScanner = (): void => {
    if (scannerCleanup || !document.documentElement) {
      return;
    }

    scannerCleanup = scanAndAttach({
      root: document,
      attach: attachElement,
      detach: detachElement,
    });
  };

  if (document.readyState === "loading") {
    const onReady = (): void => {
      startScanner();
      document.removeEventListener("DOMContentLoaded", onReady);
    };

    document.addEventListener("DOMContentLoaded", onReady);
    readyListener = () =>
      document.removeEventListener("DOMContentLoaded", onReady);
  } else {
    startScanner();
  }

  return {
    attach: attachElement,
    detach: detachElement,
    destroy() {
      readyListener?.();
      scannerCleanup?.();
      scannerCleanup = undefined;

      for (const trigger of Array.from(attachedTriggers.values())) {
        trigger.destroy();
      }

      attachedTriggers.clear();
      fetcher.clearCache();
    },
  };
}

export function init(config: EmbedConfig): typeof LibrariumPreview {
  if (!config.apiBase?.trim()) {
    throw new Error("LibrariumPreview.init requires an apiBase value");
  }

  controller?.destroy();
  controller = createController(normalizeConfig(config));
  return LibrariumPreview;
}

export function attach(
  element: HTMLElement,
  identifier: string,
  type?: PreviewEntityType,
): void {
  const resolvedTarget = resolvePreviewTarget(identifier, type);

  if (!resolvedTarget) {
    throw new Error("LibrariumPreview.attach requires a valid identifier");
  }

  if (!controller) {
    throw new Error("LibrariumPreview.init must be called before attach");
  }

  controller.attach(element, resolvedTarget);
}

export function detach(element: HTMLElement): void {
  controller?.detach(element);
}

export function destroy(): void {
  controller?.destroy();
  controller = undefined;
}

function autoInitFromCurrentScript(): void {
  const currentScript = document.currentScript;

  if (!(currentScript instanceof HTMLScriptElement)) {
    return;
  }

  const apiBase = currentScript.dataset.apiBase?.trim();
  if (!apiBase) {
    return;
  }

  init({ apiBase });
}

export const LibrariumPreview = {
  init,
  attach,
  detach,
  destroy,
};

if (typeof window !== "undefined") {
  window.LibrariumPreview = LibrariumPreview;
  autoInitFromCurrentScript();
}

declare global {
  interface Window {
    LibrariumPreview: typeof LibrariumPreview;
  }
}
